import { fetchRequestSend } from '../../helpers/common-helpers';
import l10n from '../../lang/locallang';

/**
 * Sidebar description card. The editor is built on the first `Edit` and destroyed on Save/Cancel, so a
 * reader never loads it, and nothing reaches the server until Save is clicked.
 */
export default class SidebarDescriptionEditor {
    constructor() {
        this.classList = {
            rootClass: 'js-sidebar-description',
            contentClass: 'js-sidebar-description-content',
            editorClass: 'js-sidebar-description-editor',
            toolbarClass: 'js-sidebar-description-toolbar',
            fieldClass: 'js-sidebar-description-field',
            editClass: 'js-sidebar-description-edit',
            cancelClass: 'js-sidebar-description-cancel',
            saveClass: 'js-sidebar-description-save',
            visualClass: 'js-sidebar-description-visual',
            htmlClass: 'js-sidebar-description-html',
            errorClass: 'js-sidebar-description-error',
            resizeClass: 'js-sidebar-description-resize',
            activeState: 'is-active',
        };

        this.root = document.querySelector(`.${this.classList.rootClass}`);

        if (!this.root) {
            return;
        }

        this.contentEl = this.root.querySelector(`.${this.classList.contentClass}`);
        this.editBtn = this.root.querySelector(`.${this.classList.editClass}`);

        // No Edit button means a viewer who may only read — the read markup needs no behaviour.
        if (!this.contentEl || !this.editBtn) {
            return;
        }

        this.editorEl = this.root.querySelector(`.${this.classList.editorClass}`);
        this.toolbarEl = this.root.querySelector(`.${this.classList.toolbarClass}`);
        this.fieldEl = this.root.querySelector(`.${this.classList.fieldClass}`);
        this.cancelBtn = this.root.querySelector(`.${this.classList.cancelClass}`);
        this.saveBtn = this.root.querySelector(`.${this.classList.saveClass}`);
        this.visualBtn = this.root.querySelector(`.${this.classList.visualClass}`);
        this.htmlBtn = this.root.querySelector(`.${this.classList.htmlClass}`);
        this.errorEl = this.root.querySelector(`.${this.classList.errorClass}`);
        this.resizeEl = this.root.querySelector(`.${this.classList.resizeClass}`);
        // Optional: only an editable card with nothing saved renders one.
        this.placeholderEl = this.root.querySelector('.js-sidebar-description-placeholder');

        // Breathing room kept below the buttons when a drag grows the field to the edge of the viewport.
        this.resizeViewportGap = 12;

        this.editor = null;
        this.isHtmlView = false;

        // The editor's own first output — what Save compares against, so a pure normalisation pass is
        // never written back as if the user had edited.
        this.initialEditorHtml = '';

        this.$onEdit = this.onEdit.bind(this);
        this.$onCancel = this.onCancel.bind(this);
        this.$onSave = this.onSave.bind(this);
        this.$onVisual = this.onVisual.bind(this);
        this.$onHtml = this.onHtml.bind(this);
        this.$onResizeStart = this.onResizeStart.bind(this);
        this.$onResizeMove = this.onResizeMove.bind(this);
        this.$onResizeEnd = this.onResizeEnd.bind(this);
        this.$onKeydown = this.onKeydown.bind(this);

        this.editBtn.addEventListener('click', this.$onEdit);
        this.cancelBtn.addEventListener('click', this.$onCancel);
        this.saveBtn.addEventListener('click', this.$onSave);
        this.visualBtn.addEventListener('click', this.$onVisual);
        this.htmlBtn.addEventListener('click', this.$onHtml);

        // On the card, not on `document`: an inline editor is not a modal, so Esc pressed anywhere else
        // on the page belongs to whatever layer it was pressed in.
        this.root.addEventListener('keydown', this.$onKeydown);

        // The handle is an enhancement — a host that renders the card without it still edits and saves.
        if (this.resizeEl) {
            this.resizeEl.addEventListener('pointerdown', this.$onResizeStart);
        }
    }

    /**
     * @returns {HTMLElement|null} The box the drag resizes — the code area in HTML view, the editable
     *                             one otherwise.
     */
    sizedEl() {
        return this.editorEl.querySelector(this.isHtmlView ? 'textarea.se-code-viewer' : '.sun-editor-editable');
    }

    /**
     * @param {PointerEvent} event
     */
    onResizeStart(event) {
        const el = this.sizedEl();

        if (!el) {
            return;
        }

        // Measured before anything is touched: the room below the buttons is what the drag may claim, and
        // it has to be read while the card is still at its resting height.
        const startHeight = el.getBoundingClientRect().height;
        const roomBelow = window.innerHeight - this.saveBtn.getBoundingClientRect().bottom - this.resizeViewportGap;

        // Pin the height first, then lift the stylesheet's ceiling. That ceiling is what keeps the
        // auto-height field from pushing Save off screen, and the field sits on it whenever the copy is
        // long — so without lifting it the handle could only ever shrink. Pinning first stops the field
        // from jumping to its content height the moment the cap goes.
        el.style.height = `${Math.round(startHeight)}px`;
        el.style.maxHeight = 'none';

        this.resizeState = {
            el,
            // Where inside the handle it was grabbed. The move handler re-measures the field instead of
            // accumulating a delta: growing the field gives the page a scrollbar, and any scroll during
            // the drag would otherwise leave the strip trailing behind the cursor.
            grabOffset: event.clientY - this.resizeEl.getBoundingClientRect().top,
            min: parseFloat(getComputedStyle(el).minHeight) || 0,
            max: startHeight + Math.max(0, roomBelow),
        };

        event.preventDefault();
        document.addEventListener('pointermove', this.$onResizeMove);
        document.addEventListener('pointerup', this.$onResizeEnd);
        document.addEventListener('pointercancel', this.$onResizeEnd);
    }

    /**
     * @param {PointerEvent} event
     */
    onResizeMove(event) {
        const {
            el,
            grabOffset,
            min,
            max,
        } = this.resizeState;
        const fieldTop = el.getBoundingClientRect().top;
        const height = Math.min(Math.max(event.clientY - grabOffset - fieldTop, min), max);

        el.style.height = `${Math.round(height)}px`;
    }

    onResizeEnd() {
        this.resizeState = null;
        document.removeEventListener('pointermove', this.$onResizeMove);
        document.removeEventListener('pointerup', this.$onResizeEnd);
        document.removeEventListener('pointercancel', this.$onResizeEnd);
    }

    /**
     * Editor options. The filtering keys are measured against real stored descriptions: without them the
     * editor strips our own classes, rewrites `<b>`, drops inline styles and unwraps nested `<div>`
     * (which collapses the two-column descriptions).
     *
     * @returns {object}
     */
    editorOptions() {
        return {
            plugins: { list_bulleted: SUNEDITOR.plugins.list_bulleted },
            // `codeView` has to be registered or the code area is never built and the HTML view no-ops;
            // our own switch drives it, so the button itself is hidden in CSS.
            buttonList: [['bold', 'italic', 'list_bulleted', 'codeView']],
            toolbar_container: this.toolbarEl,
            statusbar: false,
            // The field grows with the copy; its floor and the code view's live in the stylesheet,
            // because `minHeight` is not applied to the editable area.
            height: 'auto',
            value: this.contentEl.innerHTML,
            placeholder: l10n.sidebarDescription.placeholder,
            allowedClassName: '.+',
            attributeWhitelist: { '*': 'id|style|data-[^\\s]+' },
            tagStyles: {
                '@text': 'font-family|font-size|color|background-color|width|height|text-decoration|font-weight|font-style|letter-spacing|white-space',
                '@line': 'text-align|margin|margin-left|margin-right|line-height|padding|padding-left|text-indent',
            },
            convertTextTags: {
                bold: 'b',
                italic: 'i',
                underline: 'u',
                strike: 's',
            },
            formatBlock: 'DIV',
            // `i` out of the text-style set keeps empty `<i class="icon-*">` glyphs alive: they are
            // otherwise dropped as empty inline nodes, and `allowedEmptyTags` does not cover that path.
            __textStyleTags: 'strong|span|font|b|var|em|u|ins|s|strike|del|sub|sup|mark|a|label|code|summary',
        };
    }

    openEditor() {
        if (this.editor || typeof SUNEDITOR === 'undefined') {
            return;
        }

        // Visible before create: the editor measures its own box on build, and a `display: none`
        // ancestor gives it nothing to measure.
        this.setEditing(true);

        this.editor = SUNEDITOR.create(this.fieldEl, this.editorOptions());
        this.initialEditorHtml = this.editor.$.html.get();
        this.isHtmlView = false;
        this.setViewState();
        this.hideError();

        // `Edit` is hidden the moment the editor opens, so focus has to be handed on or it drops to
        // `body` — and Esc, which the card listens for on itself, would never reach it. The element, not
        // `focusManager.focus()`: the vendor call bails while the document itself is not focused.
        const editable = this.editorEl.querySelector('.sun-editor-editable');

        if (editable) {
            editable.focus();
        }
    }

    closeEditor() {
        if (this.editor) {
            this.editor.destroy();
            this.editor = null;
        }

        this.setEditing(false);
        this.hideError();
    }

    /**
     * @param {boolean} isEditing
     */
    setEditing(isEditing) {
        this.contentEl.hidden = isEditing;
        this.editBtn.hidden = isEditing;
        this.editorEl.hidden = !isEditing;
        this.cancelBtn.hidden = !isEditing;
        this.saveBtn.hidden = !isEditing;
        this.saveBtn.disabled = false;

        if (this.placeholderEl) {
            this.placeholderEl.hidden = isEditing || this.contentEl.textContent.trim() !== '';
        }
    }

    /**
     * @returns {string} Current content. In the HTML view `html.get()` returns the stale WYSIWYG copy,
     *                   and `html.set()` there only writes back into the code area — leaving the view is
     *                   the only thing that commits the typed markup. So leave it and re-enter in the
     *                   same task: nothing paints in between, where staying switched would flash the
     *                   visual editor for the length of the request and, on failure, drop the user out
     *                   of the view they were fixing.
     */
    getHtml() {
        if (!this.isHtmlView) {
            return this.editor.$.html.get();
        }

        this.editor.$.viewer.codeView(false);
        const html = this.editor.$.html.get();

        this.editor.$.viewer.codeView(true);

        return html;
    }

    /**
     * @param {boolean} isHtml
     */
    setView(isHtml) {
        this.isHtmlView = isHtml;
        this.editor.$.viewer.codeView(isHtml);
        this.setViewState();

        if (isHtml) {
            // The editor hands the code area a caret at the end of the markup, which scrolls it to the
            // bottom — the top of the description is what someone opening this view wants to see.
            const code = this.editorEl.querySelector('textarea.se-code-viewer');

            if (code) {
                code.setSelectionRange(0, 0);
                code.scrollTop = 0;
            }
        }
    }

    setViewState() {
        this.visualBtn.classList.toggle(this.classList.activeState, !this.isHtmlView);
        this.htmlBtn.classList.toggle(this.classList.activeState, this.isHtmlView);
    }

    /**
     * @param {string} message
     */
    showError(message) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
    }

    hideError() {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
    }

    onEdit() {
        this.openEditor();
    }

    onCancel() {
        this.closeEditor();
    }

    /**
     * @param {KeyboardEvent} event
     */
    onKeydown(event) {
        if (event.key !== 'Escape' || !this.editor) {
            return;
        }

        // The key ends here, so an outer shell listening on `document` does not close on the same press.
        event.stopPropagation();
        this.closeEditor();
        this.editBtn.focus();
    }

    onVisual() {
        if (this.editor) {
            this.setView(false);
        }
    }

    onHtml() {
        if (this.editor) {
            this.setView(true);
        }
    }

    async onSave() {
        if (!this.editor) {
            return;
        }

        const html = this.getHtml();

        // Opened and closed without touching anything: keep the stored markup exactly as it is.
        if (html === this.initialEditorHtml) {
            this.closeEditor();

            return;
        }

        this.saveBtn.disabled = true;
        this.hideError();

        try {
            const response = await fetchRequestSend(saveCourseDescriptionURL, 'POST', {
                description: html,
                course_id: courseID,
            });

            // Repaint from what the server stored — it sanitises on write, so this is the only copy
            // that matches the next page load.
            this.contentEl.innerHTML = typeof response?.description === 'string' ? response.description : html;
            this.closeEditor();
        } catch (error) {
            this.saveBtn.disabled = false;
            this.showError(l10n.sidebarDescription.saveError);
        }
    }
}
